import { forwardRef } from "react";

import EventCard from "./EventCard";
import HoursDay from "./HoursDay";
import AvailableTimeCard from "./AvailableTimeCard";
import dates from "componentsv2/tools/dates";
import { classNames } from "tools/ui";
import utils from "../services/appointment";
import { Spanish } from "flatpickr/dist/l10n/es";
import { useContext } from "../store/useContext";

function DaysHeader({ newDays, onClickDay, currentDate }) {
  const fmCalendar = Spanish;
  const days = fmCalendar.weekdays.shorthand;
  const dateString = dates.fmt(currentDate, "yyyy-MM-dd");

  return (
    <div className="sticky top-0 z-30 flex-none bg-gray-100 shadow ring-1 ring-black/5 sm:pr-8">
      <div className="grid grid-cols-7 text-sm/6 text-gray-500 sm:hidden">
        {newDays.map((day, idx) => (
          <button
            key={day.dayString}
            type="button"
            onClick={() => onClickDay(day)}
            className="flex flex-col items-center pb-3 pt-2"
          >
            {days[idx][0]}
            <span
              className={classNames(
                "mt-1 flex size-8 items-center justify-center font-semibold",
                day.isToday && day.dayString !== dateString
                  ? "text-indigo-600"
                  : day.isToday
                    ? "rounded-full bg-indigo-600 text-white"
                    : day.dayString === dateString
                      ? "rounded-full bg-gray-900 text-white"
                      : "text-gray-900",
              )}
            >
              {dates.fmt(day.day, "d")}
            </span>
          </button>
        ))}
      </div>

      <div className="-mr-px hidden grid-cols-7 divide-x divide-gray-100 border-r border-gray-100 text-sm/6 text-gray-500 sm:grid">
        <div className="col-end-1 w-14" />
        {newDays.map((day, idx) => (
          <div
            key={day.dayString}
            className="flex items-center justify-center py-3 space-x-1"
          >
            <span className="flex items-baseline">{days[idx]}</span>
            <span
              className={classNames(
                "mt-1 flex size-8 items-center justify-center font-semibold",
                day.isToday && day.dayString !== dateString
                  ? "text-indigo-600"
                  : day.isToday
                    ? "rounded-full bg-indigo-600 text-white"
                    : day.dayString === dateString
                      ? "rounded-full bg-gray-900 text-white"
                      : "text-gray-900",
              )}
            >
              {dates.fmt(day.day, "d")}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const ForwardedHoursDay = forwardRef(HoursDay);

function WeekView({ newDays, handleDay, handleEvent }) {
  const { context, updateContext } = useContext();
  const { currentDate, availableTime, currentOwner } = context;
  const cdate = dates.fmt(currentDate, "yyyy-MM-dd");
  const { startHour, endHour } = utils.getHourRange(
    context.minStartTime,
    context.maxEndTime,
  );

  const isSingleColumn = window.innerWidth < 768;
  const items = [];

  if (newDays && newDays?.length > 0) {
    for (const day of newDays) {
      if (isSingleColumn && day.dayString === cdate) {
        for (const item of day.events) {
          items.push(item);
        }
      } else {
        for (const item of day.events) {
          items.push(item);
        }
      }
    }
  }

  const stackedDays = newDays.map((day) => ({
    ...day,
    events: utils.groupOverlappingEvents(day.events || [], "week"),
  }));

  function onClickDay(day) {
    updateContext({ currentDate: day.day });
  }

  function handleClick(e) {
    const olElement = e.currentTarget.closest("ol");

    if (!olElement) return;

    const calendar = olElement;
    const rect = calendar.getBoundingClientRect();
    const scrollTop = calendar.scrollTop;
    const scrollLeft = calendar.scrollLeft;

    const relativeY = e.clientY - rect.top + scrollTop;
    const relativeX = e.clientX - rect.left + scrollLeft;

    const rowHeight = (rect.height - 28) / ((endHour - startHour) * 12);
    const gridRow = Math.floor(relativeY / rowHeight) + 2; // Ajustar gridRow (+2 por cabecera)

    // Calcular hora ajustando por la hora de inicio visible
    const totalMinutes = Math.floor((gridRow - 4) * 5) + startHour * 60;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    // Calcular día basado en el índice de columna
    const columns = 7;
    const columnWidth = rect.width / columns;
    const dayIndex = Math.floor(relativeX / columnWidth);

    let day = new Date(newDays[dayIndex].day);
    if (isSingleColumn) {
      day = new Date(currentDate);
    }
    day.setHours(hours);
    day.setMinutes(minutes);
    const start_date = day;

    handleDay(e, day, start_date);
  }

  return (
    <div className="flex max-h-[calc(100vh-8rem)] flex-col">
      <div className="isolate flex flex-auto flex-col overflow-auto bg-white mb-4 agenda-container">
        <div className="flex max-w-full flex-none flex-col sm:max-w-none md:max-w-full">
          <DaysHeader
            newDays={newDays}
            currentDate={currentDate}
            onClickDay={onClickDay}
          />

          <div className="flex flex-auto">
            <div className="sticky left-0 z-10 w-14 flex-none bg-white ring-1 ring-gray-100" />
            <div className="grid flex-auto grid-cols-1 grid-rows-1">
              {/* Horizontal lines */}
              <ForwardedHoursDay />

              {/* Vertical lines */}
              <div className="col-start-1 col-end-2 row-start-1 hidden grid-cols-7 grid-rows-1 divide-x divide-gray-100 sm:grid sm:grid-cols-7">
                <div className="col-start-1 row-span-full" />
                <div className="col-start-2 row-span-full" />
                <div className="col-start-3 row-span-full" />
                <div className="col-start-4 row-span-full" />
                <div className="col-start-5 row-span-full" />
                <div className="col-start-6 row-span-full" />
                <div className="col-start-7 row-span-full" />
                <div className="col-start-8 row-span-full w-8" />
              </div>
              {/* Available Time */}
              <ol
                className="col-start-1 col-end-2 row-start-1 grid grid-cols-1 sm:grid-cols-7 sm:pr-8"
                style={{
                  gridTemplateRows: `1.75rem repeat(${(endHour - startHour) * 12}, minmax(0, 1fr)) auto`,
                }}
              >
                {currentOwner &&
                  availableTime.map((item) => (
                    <AvailableTimeCard
                      key={item.id}
                      time={item}
                      view="week"
                      isSingleColumn={isSingleColumn}
                      currentDate={currentDate}
                      handleClick={handleClick}
                    />
                  ))}
              </ol>
              {/* Events */}
              <div
                className="col-start-1 col-end-2 row-start-1 grid grid-cols-1 sm:grid-cols-7 sm:pr-8"
                style={{
                  gridTemplateRows: `1.75rem repeat(${(endHour - startHour) * 12}, minmax(0, 1fr)) auto`,
                }}
              >
                {stackedDays.map((day) =>
                  day.events.map(
                    (event) =>
                      (!isSingleColumn || day.dayString === cdate) && (
                        <EventCard
                          key={event.id}
                          event={event}
                          handleEvent={handleEvent}
                          view="week"
                        />
                      ),
                  ),
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WeekView;
